import { ChangeEvent } from 'react';
import { cn, Pagination, PaginationItem, PaginationItemRenderProps, PaginationItemType, Select, SelectItem } from '@ds';
import { MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { BsThreeDots } from 'react-icons/bs';
import { NameByValue } from '@/types';
import { getPluralLabel } from './util';

const PER_PAGE_SELECT_ITEMS: NameByValue<number>[] = [
  { value: 10, name: '10 / page' },
  { value: 15, name: '15 / page' },
  { value: 20, name: '20 / page' },
  { value: 100, name: '100 / page' },
];

const COMMON_STYLES =
  'w-8 h-8 border-1 border-base-70 rounded-small flex justify-center items-center text-black bg-white shadow-none text-s-sb hover:bg-white! hover:border-black';
const COMMON_ARROW_STYLES =
  'data-[disabled=true]:border-base-40 data-[disabled=true]:bg-base-80 data-[disabled=true]:text-base-30';

export const renderPaginationItem = ({
  ref,
  key,
  value,
  isBefore,
  total,
  activePage,
  isActive,
  onNext,
  onPrevious,
  setPage,
  className,
  getAriaLabel,
}: PaginationItemRenderProps) => {
  if (value === PaginationItemType.NEXT) {
    return (
      <PaginationItem
        key={PaginationItemType.NEXT}
        className={cn(className, `${COMMON_STYLES} ${COMMON_ARROW_STYLES}`)}
        getAriaLabel={getAriaLabel}
        isDisabled={activePage === total}
        value={value}
        onPress={onNext}
      >
        <MdOutlineKeyboardArrowRight size={24} />
      </PaginationItem>
    );
  }

  if (value === PaginationItemType.PREV) {
    return (
      <PaginationItem
        key={key}
        className={cn(className, `${COMMON_STYLES} ${COMMON_ARROW_STYLES}`)}
        getAriaLabel={getAriaLabel}
        isDisabled={activePage === 1}
        value={value}
        onPress={onPrevious}
      >
        <MdOutlineKeyboardArrowRight size={24} className="rotate-180" />
      </PaginationItem>
    );
  }

  if (value === PaginationItemType.DOTS) {
    return (
      <PaginationItem
        key={PaginationItemType.DOTS + isBefore}
        className="w-8 h-8 flex justify-center items-center bg-transparent text-base-60 pointer-events-none"
        getAriaLabel={getAriaLabel}
        value={value}
      >
        <BsThreeDots size={24} />
      </PaginationItem>
    );
  }

  return (
    <PaginationItem
      ref={ref}
      key={value}
      isActive={isActive}
      className={cn(className, COMMON_STYLES)}
      getAriaLabel={getAriaLabel}
      onPress={() => setPage(value)}
    >
      {value}
    </PaginationItem>
  );
};

type TablePaginationProps = {
  isLoading: boolean;
  totalItems: number;
  totalPages: number;
  page: number;
  itemsPerPage: number;
  dataName?: string;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (page: number) => void;
};

export const TablePagination = ({
  isLoading,
  totalItems,
  totalPages,
  page,
  itemsPerPage,
  dataName = 'item',
  onPageChange,
  onItemsPerPageChange,
}: TablePaginationProps) => {
  const handlePerPageChange = (e: ChangeEvent<HTMLSelectElement>) => {
    onItemsPerPageChange(Number(e.target.value));
  };

  return (
    <div className="flex justify-between items-center">
      <span className="text-m-sb text-base-20">Total {getPluralLabel(totalItems, dataName)}</span>
      {!isLoading && (
        <Pagination
          size="sm"
          page={page}
          onChange={onPageChange}
          total={totalPages}
          showControls
          renderItem={renderPaginationItem}
          classNames={{
            wrapper: 'gap-2',
            cursor: 'text-s-sb! rounded-small bg-black text-white',
          }}
        />
      )}
      <div className="w-[120px]">
        <Select
          size="sm"
          radius="full"
          aria-label="Items per page"
          selectedKeys={[`${itemsPerPage}`]}
          selectionMode="single"
          disallowEmptySelection
          onChange={handlePerPageChange}
          classNames={{
            trigger: 'shadow-none bg-base-60',
            value: 'text-m-sb! group-data-[has-value=true]:text-base-20',
            selectorIcon: 'text-base-20!',
          }}
        >
          {PER_PAGE_SELECT_ITEMS.map((item) => (
            <SelectItem key={item.value}>{item.name}</SelectItem>
          ))}
        </Select>
      </div>
    </div>
  );
};
